/**
 * Truth Layer API service
 *
 * Wraps the truth layer / fleet-intelligence endpoints for the Vehicle Scene
 * Timeline. Maps backend v0.4 response shapes to frontend graph-first types.
 */
import type {
  VehicleSceneTimelineResponse,
  MovementSegment,
  OrderOverlay,
  PlannedOrderOverlay,
  DeliveryMarker,
  DriverOverlay,
  FuelOverlay,
  AttributionSlice,
  GraphEdgesResponse,
  GraphExplainResponse,
  RelationshipType,
} from '../types/vehicleSceneTimeline';
import { API_ENDPOINTS } from '../config/api';
import { authenticatedFetch } from '../utils/authenticatedFetch';

// ── Base URLs (from centralised config) ─────────────────────────────────────

const ZEECORE = API_ENDPOINTS.base; // '' = same origin (see config/api.ts)

// ── Helpers ─────────────────────────────────────────────────────────────────

const jsonFetch = async <T>(url: string): Promise<T> => {
  const res = await authenticatedFetch(url);
  if (!res.ok) throw new Error(`HTTP ${res.status}: ${res.statusText}`);
  return res.json() as Promise<T>;
};

// ── Vehicle list ────────────────────────────────────────────────────────────

export interface VehicleListItem {
  id: string;
  label: string;
}

/**
 * Fetches the live vehicle list from the fleet-intelligence endpoint
 * and normalises it to `{ id, label }` for the ControlsBar dropdown.
 */
export const fetchVehicleList = async (): Promise<VehicleListItem[]> => {
  try {
    const url = API_ENDPOINTS.fleetIntelligence.vehicles.list();
    const raw = await jsonFetch<{
      vehicles: Array<{
        entityId: string;
        identifiers?: Array<{ type: string; value: string }>;
        source?: string;
      }>;
    }>(url);

    return raw.vehicles.map(v => {
      // Build a human-readable label from identifiers (prefer registration)
      const reg = v.identifiers?.find(
        i => i.type === 'registration' || i.type === 'vrn',
      );
      const name = v.identifiers?.find(i => i.type === 'name' || i.type === 'alias');
      const label = [reg?.value, name?.value].filter(Boolean).join(' – ') || v.entityId;
      return { id: v.entityId, label };
    });
  } catch {
    console.warn('[TruthLayerAPI] Vehicle list endpoint unavailable');
    return [];
  }
};

// ── Response mapping helpers ────────────────────────────────────────────────

/**
 * Hash-like id generator for overlay windows that lack an edge_id.
 * Uses a simple deterministic string so we get stable React keys.
 */
const syntheticEdgeId = (prefix: string, entityId: string, from: string, to: string) =>
  `${prefix}__${entityId}__${from}__${to}`;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function mapSegments(raw: any[]): MovementSegment[] {
  return raw.map(s => ({
    segment_id: s.segment_id,
    start_ts: s.start_ts,
    end_ts: s.end_ts,
    type: s.type,
    source: s.source,
    completeness: s.completeness,
    confidence: s.confidence ?? undefined,
    evidence_refs: s.evidence_refs ?? [],
    notes: s.notes ?? undefined,
  }));
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function mapOrders(raw: any[], vehicleId: string): OrderOverlay[] {
  return (raw ?? []).map(o => ({
    edge_id: o.edge_id ?? syntheticEdgeId('order', o.order_entity_id, o.from_ts, o.to_ts),
    edge_group_key: o.edge_group_key ?? `ORDER_ON_VEHICLE:${o.order_entity_id}:${vehicleId}`,
    relationship_type: 'ORDER_ON_VEHICLE' as const,
    from_entity_id: vehicleId,
    to_entity_id: o.order_entity_id,
    from_ts: o.from_ts,
    to_ts: o.to_ts,
    confidence: o.confidence ?? 0.5,
    authority_level: o.authority_level ?? 'DECLARED' as const,
    derivation: o.derivation ?? 'DECLARED_VEHICLE_LINK',
    evidence_refs: o.evidence_refs ?? [],
    source_order_key: o.source_order_key,
    origin_postcode: o.origin_postcode,
    destination_postcode: o.destination_postcode,
    label: o.source_order_key ?? o.order_entity_id,
    // Lifecycle enrichment
    entry_state: o.entry_state,
    exit_state: o.exit_state,
    pickup_evidence: o.pickup_evidence,
    delivery_evidence: o.delivery_evidence,
    delivery_confidence_score: o.delivery_confidence_score,
    actual_load_hub_name: o.actual_load_hub_name,
  }));
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function mapPlannedOrders(raw: any[], vehicleId: string): PlannedOrderOverlay[] {
  return (raw ?? []).map(p => ({
    edge_id: p.edge_id ?? syntheticEdgeId('planned', p.order_entity_id, p.from_ts, p.to_ts ?? 'open'),
    edge_group_key: p.edge_group_key ?? `ORDER_PLANNED_ON_VEHICLE:${p.order_entity_id}:${vehicleId}`,
    relationship_type: 'ORDER_PLANNED_ON_VEHICLE' as const,
    from_entity_id: vehicleId,
    to_entity_id: p.order_entity_id,
    from_ts: p.from_ts,
    to_ts: p.to_ts ?? new Date().toISOString(),
    confidence: p.confidence ?? 0.5,
    authority_level: p.authority_level ?? 'DECLARED' as const,
    derivation: p.derivation ?? 'PLANNING_ALLOCATION',
    evidence_refs: p.evidence_refs ?? [],
    source_order_key: p.source_order_key,
    origin_postcode: p.origin_postcode,
    destination_postcode: p.destination_postcode,
    label: p.source_order_key ?? p.order_entity_id,
  }));
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function mapDeliveries(raw: any[]): DeliveryMarker[] {
  return (raw ?? []).map(d => ({
    event_id: d.event_id ?? `delivery_${d.order_entity_id}`,
    order_entity_id: d.order_entity_id,
    vehicle_entity_id: d.vehicle_entity_id,
    source_order_key: d.source_order_key,
    occurred_at: d.occurred_at,
    confidence: d.confidence ?? 0.5,
    authority_level: d.authority_level ?? 'OBSERVED',
    derivation: d.derivation ?? 'TMS_DELIVERY_OBSERVATION',
    evidence_refs: d.evidence_refs ?? [],
    destination_postcode: d.destination_postcode,
    pod_reference: d.pod_reference,
    delivery_evidence: d.delivery_evidence,
  }));
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function mapDrivers(raw: any[], vehicleId: string): DriverOverlay[] {
  return (raw ?? []).map(d => ({
    edge_id: d.edge_id ?? syntheticEdgeId('driver', d.driver_entity_id, d.from_ts, d.to_ts),
    edge_group_key: `DRIVER_ON_VEHICLE:${d.driver_entity_id}:${vehicleId}`,
    relationship_type: 'DRIVER_ON_VEHICLE' as const,
    from_entity_id: d.driver_entity_id,  // driver is "from" — gives distinct colour per driver
    to_entity_id: vehicleId,             // vehicle is "to"
    from_ts: d.from_ts,
    to_ts: d.to_ts,
    confidence: d.confidence ?? 0.5,
    authority_level: d.authority_level ?? 'DECLARED' as const,
    derivation: d.derivation ?? 'SHIFT_OVERLAP',
    evidence_refs: d.evidence_refs ?? [],
    source_driver_key: d.source_driver_key,
    label: d.source_driver_key ?? d.driver_entity_id,
  }));
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function mapFuel(raw: any[], vehicleId: string): FuelOverlay[] {
  return (raw ?? []).map(f => ({
    edge_id: f.edge_id ?? syntheticEdgeId('fuel', f.fuel_entity_id, f.from_ts, f.to_ts),
    edge_group_key: `FUEL_ON_VEHICLE:${f.fuel_entity_id}:${vehicleId}`,
    relationship_type: 'FUEL_ON_VEHICLE' as const,
    from_entity_id: f.fuel_entity_id,  // fuel transaction is "from" — matches graph edge direction
    to_entity_id: vehicleId,           // vehicle is "to"
    from_ts: f.from_ts,
    to_ts: f.to_ts,
    confidence: f.confidence ?? 0.5,
    authority_level: f.authority_level ?? 'DECLARED' as const,
    derivation: f.derivation ?? 'DECLARED_VEHICLE_LINK',
    evidence_refs: f.evidence_refs ?? [],
    fuel_litres: f.fuel_litres,
    fuel_event_type: f.fuel_event_type,
    label: f.source_fuel_key ?? f.fuel_entity_id,
  }));
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function mapSlices(raw: any[]): AttributionSlice[] {
  return (raw ?? []).map(s => ({
    slice_id: s.slice_id,
    start_ts: s.start_ts,
    end_ts: s.end_ts,
    active_order_ids: s.order_entity_ids ?? s.active_order_ids ?? [],
    active_driver_id: s.driver_entity_id ?? s.active_driver_id ?? null,
    active_fuel_ids: s.fuel_entity_ids ?? s.active_fuel_ids ?? [],
    movement_segment_ids: s.segment_id
      ? [s.segment_id]
      : s.movement_segment_ids ?? [],
    movement_state: s.type ?? s.movement_state ?? 'UNKNOWN',
    confidence: s.confidence ?? 0,
    evidence_count:
      s.evidence_count ?? (s.evidence_refs ? s.evidence_refs.length : 0),
    edge_ids: s.edge_ids ?? [],
  }));
}

/**
 * Maps the raw backend v0.4 JSON response to the frontend
 * VehicleSceneTimelineResponse shape.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function mapBackendResponse(raw: any): VehicleSceneTimelineResponse {
  const vehicleId: string = raw.vehicle_entity_id;
  const overlays = raw.overlays ?? { orders: [], drivers: [], fuel: [] };

  return {
    vehicle_entity_id: vehicleId,
    window: {
      from: raw.window?.from,
      to: raw.window?.to,
      asOf: raw.window?.asOf,
    },
    segments: mapSegments(raw.segments ?? []),
    overlays: {
      orders: mapOrders(overlays.orders, vehicleId),
      planned_orders: mapPlannedOrders(overlays.planned_orders, vehicleId),
      deliveries: mapDeliveries(overlays.deliveries),
      drivers: mapDrivers(overlays.drivers, vehicleId),
      fuel: mapFuel(overlays.fuel, vehicleId),
    },
    attribution_slices: mapSlices(raw.attribution_slices ?? []),
    meta: {
      computed_at: raw.meta?.computed_at ?? new Date().toISOString(),
      projection_version: raw.meta?.projection_version ?? 'v0.4',
      authority_summary: raw.meta?.authority_summary
        ? {
            observed_count: raw.meta.authority_summary.by_completeness?.Observed ?? 0,
            declared_count: raw.meta.authority_summary.by_completeness?.Declared ?? 0,
            inferred_count: raw.meta.authority_summary.by_completeness?.Inferred ?? 0,
            unknown_gap_count: raw.meta.authority_summary.by_completeness?.Unknown ?? 0,
          }
        : undefined,
      provenance_summary: raw.meta?.provenance_summary
        ? {
            total_evidence_events: raw.meta.provenance_summary.count ?? 0,
          }
        : undefined,
      graph_summary: raw.meta?.orders_summary || raw.meta?.drivers_summary || raw.meta?.fuel_summary
        ? {
            total_edges:
              (raw.meta.orders_summary?.unique_order_count ?? 0) +
              (raw.meta.drivers_summary?.unique_driver_count ?? 0) +
              (raw.meta.fuel_summary?.unique_fuel_count ?? 0),
            by_relationship: {
              ORDER_ON_VEHICLE: raw.meta.orders_summary?.unique_order_count ?? 0,
              DRIVER_ON_VEHICLE: raw.meta.drivers_summary?.unique_driver_count ?? 0,
              FUEL_ON_VEHICLE: raw.meta.fuel_summary?.unique_fuel_count ?? 0,
            },
          }
        : undefined,
    },
    data_quality: raw.data_quality
      ? {
          completeness: raw.data_quality.completeness ?? 0,
          issues: (raw.data_quality.issues ?? []).map((i: any) => ({
            type: i.type,
            severity: i.severity,
            message: i.message,
            detail: i.detail,
          })),
        }
      : undefined,
  };
}

// ── Endpoints ───────────────────────────────────────────────────────────────

/**
 * GET /truth/vehicles/:vehicleId/movement?from&to&asOf
 * Returns v0.4 payload with segments, graph overlays, attribution slices.
 */
export const fetchVehicleSceneTimeline = async (
  vehicleId: string,
  from: string,
  to: string,
  asOf?: string | null,
): Promise<VehicleSceneTimelineResponse> => {
  try {
    const url = API_ENDPOINTS.fleetIntelligence.vehicles.movementTimeline(
      vehicleId,
      { from, to, asOf: asOf ?? undefined },
    );
    const raw = await jsonFetch<unknown>(url);
    return mapBackendResponse(raw);
  } catch (err) {
    console.warn('[TruthLayerAPI] Backend unavailable', err);
    throw err;
  }
};

/**
 * GET /truth-browser/graph-relationships/:entityType/:entityId?from&to&relationshipType
 * Returns resolved graph edges for the query.
 */
export const fetchGraphEdges = async (
  fromEntityId: string,
  relationshipType: RelationshipType,
  from: string,
  to: string,
  _asOf?: string | null,
): Promise<GraphEdgesResponse> => {
  try {
    const url = API_ENDPOINTS.truthBrowser.graphRelationships(
      'vehicle',
      fromEntityId,
      { from, to, relationshipType },
    );
    const raw = await jsonFetch<{ edges?: unknown[] }>(url);
    // The graph-relationships endpoint may return edges in a different shape;
    // normalise to our GraphEdgesResponse.
    const edges = Array.isArray(raw.edges) ? raw.edges : Array.isArray(raw) ? raw : [];
    return {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      edges: edges as any,
      meta: { total: edges.length, query_ts: new Date().toISOString() },
    };
  } catch {
    console.warn('[TruthLayerAPI] Graph edges endpoint unavailable');
    return { edges: [], meta: { total: 0, query_ts: new Date().toISOString() } };
  }
};

/**
 * GET /truth-browser/graph-relationships/edge/:edgeId
 * Returns deeper lineage / evidence detail for a single edge.
 *
 * @param edgeId - The deterministic edge ID to explain
 * @param from   - Optional ISO 8601 start of the search window (defaults to 90 days ago on the server)
 * @param to     - Optional ISO 8601 end of the search window (defaults to now on the server)
 */
export const fetchGraphExplain = async (
  edgeId: string,
  from?: string,
  to?: string,
): Promise<GraphExplainResponse> => {
  const params = new URLSearchParams();
  if (from) params.set('from', from);
  if (to) params.set('to', to);
  const qs = params.toString() ? `?${params.toString()}` : '';
  const url = `${ZEECORE}/truth-browser/graph-relationships/edge/${encodeURIComponent(edgeId)}${qs}`;
  return jsonFetch<GraphExplainResponse>(url);
};
